import { cn } from '@/lib/utils';

interface EvaluationBarProps {
  evaluation: number;
  className?: string;
  showNumbers?: boolean;
  showPlayerLabels?: boolean;
}

export function EvaluationBar({ evaluation, className, showNumbers = true, showPlayerLabels = true }: EvaluationBarProps) {
  // Convert evaluation to win percentage (approximately)
  const getWinPercentage = (evalScore: number): number => {
    // Clamp evaluation between -1000 and 1000 centipawns
    const clampedEval = Math.max(-1000, Math.min(1000, evalScore));
    
    // Convert to approximate win percentage using sigmoid-like function
    // Formula based on chess.com's evaluation bar
    const winChance = 50 + (50 * clampedEval) / (200 + Math.abs(clampedEval));
    return Math.max(0, Math.min(100, winChance));
  };

  const formatEvaluation = (evalScore: number): string => {
    if (Math.abs(evalScore) > 900) {
      const mateIn = Math.abs(Math.round((1000 - Math.abs(evalScore)) / 10));
      return evalScore > 0 ? `+M${mateIn}` : `-M${mateIn}`;
    }
    
    const pawns = evalScore / 100;
    return pawns > 0 ? `+${pawns.toFixed(1)}` : pawns.toFixed(1);
  };

  const winPercentage = getWinPercentage(evaluation);
  const whiteAdvantage = winPercentage;

  return (
    <div className={cn("w-full", className)}>
      {/* Enhanced Evaluation Bar */}
      <div className="relative h-4 bg-muted rounded-lg overflow-hidden shadow-inner">
        {/* White advantage portion - light colors */}
        <div 
          className="absolute top-0 right-0 h-full bg-gradient-to-r from-background to-card border-r-2 border-foreground/10 transition-all duration-300 ease-out"
          style={{ width: `${whiteAdvantage}%` }}
        />
        
        {/* Black advantage portion - dark colors */}
        <div 
          className="absolute top-0 left-0 h-full bg-gradient-to-l from-foreground to-muted-foreground border-l-2 border-background/10 transition-all duration-300 ease-out"
          style={{ width: `${100 - whiteAdvantage}%` }}
        />
        
        {/* Center line indicator */}
        <div className="absolute top-0 left-1/2 w-0.5 h-full bg-border transform -translate-x-0.5 z-10" />
        
        {/* Evaluation text overlay with improved contrast */}
        {showNumbers && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className={cn(
              "text-xs font-bold font-mono px-2 py-0.5 rounded-md border backdrop-blur-sm shadow-lg",
              whiteAdvantage > 55 
                ? "text-foreground bg-background/95 border-foreground/20" 
                : whiteAdvantage < 45
                ? "text-background bg-foreground/95 border-background/20"
                : "text-foreground bg-muted/95 border-border shadow-md"
            )}>
              {formatEvaluation(evaluation)}
            </span>
          </div>
        )}
        
        {/* Subtle glow effect for extreme evaluations */}
        {(whiteAdvantage > 75 || whiteAdvantage < 25) && (
          <div className={cn(
            "absolute inset-0 rounded-lg opacity-20 animate-pulse",
            whiteAdvantage > 75 ? "bg-green-500/30" : "bg-red-500/30"
          )} />
        )}
      </div>
      
      {/* Player labels with high contrast and better positioning */}
      {showPlayerLabels && (
        <div className="flex justify-between mt-2 text-xs font-medium">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-foreground rounded-full"></div>
            <span className="text-foreground">Black</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-foreground">White</span>
            <div className="w-2 h-2 bg-background border border-foreground rounded-full"></div>
          </div>
        </div>
      )}
    </div>
  );
}